import { LanguageSupport, StreamLanguage } from '@codemirror/language';

/**
 * Expr-lang syntax definition for CodeMirror 6
 * @see https://expr-lang.org/docs/language-definition
 */
const exprLangDef = StreamLanguage.define({
  name: 'expr',

  startState() {
    return {
      inString: null as string | null, // null, '"', "'", or '`'
      inComment: false, // block comment
    };
  },

  token(stream, state) {
    // Handle block comments
    if (state.inComment) {
      if (stream.match('*/')) {
        state.inComment = false;
        return 'comment';
      }
      stream.next();
      return 'comment';
    }

    // Handle strings
    if (state.inString) {
      const quote = state.inString;
      while (!stream.eol()) {
        const ch = stream.next();
        if (
          ch === quote &&
          (quote === '`' || stream.string[stream.pos - 2] !== '\\')
        ) {
          state.inString = null;
          return 'string';
        }
      }
      return 'string';
    }

    // Skip whitespace
    if (stream.eatSpace()) return null;

    // Start of block comment
    if (stream.match('/*')) {
      state.inComment = true;
      return 'comment';
    }

    // Line comment
    if (stream.match('//')) {
      stream.skipToEnd();
      return 'comment';
    }

    // Strings
    const ch = stream.peek();
    if (ch === '"' || ch === "'" || ch === '`') {
      stream.next();
      state.inString = ch;
      return 'string';
    }

    // Numbers - hex, octal, binary, float, int
    if (
      stream.match(/^0x[0-9a-fA-F]+/) ||
      stream.match(/^0o[0-7]+/) ||
      stream.match(/^0b[01]+/) ||
      stream.match(/^\d+\.\d*(?:[eE][+-]?\d+)?/) ||
      stream.match(/^\.\d+(?:[eE][+-]?\d+)?/) ||
      stream.match(/^\d+(?:[eE][+-]?\d+)?/)
    ) {
      return 'number';
    }

    // Multi-character operators (must check before single chars)
    if (
      stream.match('==') ||
      stream.match('!=') ||
      stream.match('<=') ||
      stream.match('>=') ||
      stream.match('&&') ||
      stream.match('||') ||
      stream.match('??') ||
      stream.match('**') ||
      stream.match('..') ||
      stream.match('?.') ||
      stream.match('?:')
    ) {
      return 'operator';
    }

    // Single character operators
    if (stream.match(/^[+\-*/%^<>=!|&?:,;]/)) {
      return 'operator';
    }

    // Brackets and delimiters
    if (stream.match(/^[()[\]{}]/)) {
      return 'bracket';
    }

    // Property access
    if (stream.match('.')) {
      return 'operator';
    }

    // Predicate placeholder
    if (stream.match('#')) {
      return 'variableName.special';
    }

    // Variable reference
    if (stream.match('$')) {
      if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
        return 'variableName.special';
      }
      return 'variableName.special';
    }

    // Keywords and identifiers
    if (stream.match(/^[a-zA-Z_][a-zA-Z0-9_]*/)) {
      const word = stream.current();

      // Boolean literals
      if (word === 'true' || word === 'false') {
        return 'bool';
      }

      // Nil literal
      if (word === 'nil') {
        return 'null';
      }

      // Keywords
      const keywords = [
        'let',
        'in',
        'not',
        'and',
        'or',
        'if',
        'else',
        'contains',
        'startsWith',
        'endsWith',
        'matches',
      ];
      if (keywords.includes(word)) {
        return 'keyword';
      }

      // Built-in functions
      const builtins = [
        // String functions
        'len',
        'trim',
        'trimPrefix',
        'trimSuffix',
        'upper',
        'lower',
        'split',
        'replace',
        'repeat',
        'indexOf',
        'lastIndexOf',
        'hasPrefix',
        'hasSuffix',
        'join',
        'title',
        // Array functions
        'map',
        'filter',
        'all',
        'any',
        'one',
        'none',
        'find',
        'findIndex',
        'findLast',
        'findLastIndex',
        'sort',
        'sortBy',
        'groupBy',
        'count',
        'concat',
        'flatten',
        'reverse',
        'unique',
        'first',
        'last',
        'take',
        'drop',
        'sum',
        'mean',
        'median',
        'min',
        'max',
        'reduce',
        // Date functions
        'now',
        'duration',
        'date',
        'timezone',
        // Type functions
        'type',
        'int',
        'float',
        'string',
        'bool',
        'toJSON',
        'fromJSON',
        'toBase64',
        'fromBase64',
        // Math functions
        'abs',
        'ceil',
        'floor',
        'round',
        'sqrt',
        'log',
        'log10',
        'pow',
        // Bitwise functions
        'bitand',
        'bitor',
        'bitxor',
        'bitnot',
        'bitshl',
        'bitshr',
        'bitushr',
        // Other
        'get',
        'keys',
        'values',
        'print',
        'printf',
        'sprintf',
      ];
      if (builtins.includes(word)) {
        return 'function';
      }

      // Regular identifier
      return 'variableName';
    }

    // Unknown character - advance
    stream.next();
    return null;
  },

  languageData: {
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
  },
});

/**
 * CodeMirror language support for expr-lang expressions.
 * Provides syntax highlighting for the expr expression language.
 *
 * @see https://expr-lang.org/docs/language-definition
 *
 * @example
 * ```tsx
 * import { exprLang } from './codemirror/exprLang';
 *
 * <CodeMirror extensions={[exprLang()]} />
 * ```
 */
export function exprLang(): LanguageSupport {
  return new LanguageSupport(exprLangDef);
}

/**
 * The expr-lang StreamLanguage definition.
 * Use this if you need direct access to the language definition.
 */
export { exprLangDef };
